import {
  any,
  CredentialRequest,
  IDKit,
  type IDKitDebugReport,
  type IDKitRequest,
  type IDKitResult,
  type RpContext,
} from '@worldcoin/idkit-core';
import { ethers } from 'ethers';
import { walletAppConfig } from './config';
import { supabase } from './supabaseClient';
import {
  sendWorldTransaction,
  WorldTransactionError,
  type WorldTransactionNotice,
} from './worldMiniKit';
import { ON_CHAIN_TOKEN_ADDRESSES, worldChainProvider } from './blockchain';
import {
  clearClaimRecovery,
  clearWorldIdSessionId,
  computeClaimAuthorizationDigest,
  createClaimRecoveryRecord,
  createIdempotencyKey,
  isAuthorizationExpired,
  parseClaimAuthorizationResponse,
  readClaimRecovery,
  readWorldIdSessionId,
  selectWorldIdSessionId,
  type ClaimAuthorization,
  type ClaimFlow,
  type ClaimRecoveryRecord,
  type StorageLike,
  type WorldIdSessionId,
  writeWorldIdSessionId,
  writeClaimRecovery,
} from './worldIdClaimRecovery';

const PROOF_OF_HUMAN = 'proof_of_human' as const;
const WORLD_ID_ENVIRONMENT = 'production' as const;
const WORLD_CHAIN_ID = 480;
const WORLD_ID_DIAGNOSTIC_PREFIX = 'WID';
const ENROLLMENT_SIGNAL_PREFIX = 'bubble-claim-enrollment-v2:';
const SESSION_SIGNAL_PREFIX = 'bubble-claim-session-v2:';

const BUBBLE_CLAIM_ABI = [
  'function enrolled(address) view returns (bool)',
  'function isAuthorizationConsumed(bytes32 digest) view returns (bool)',
  'function tokenConfigs(address) view returns (bool enabled, uint256 claimAmount, uint256 cap, uint256 claimedAmount, uint64 cooldown)',
  'function lastDirectClaimAt(address token, address claimant) view returns (uint256)',
  'function paused() view returns (bool)',
  'function effectiveTimestamp() view returns (uint256)',
  'function enroll((address wallet, bytes32 nonce, uint256 deadline) authorization, bytes signature)',
  'function claim((address wallet, address token, bytes32 nonce, uint256 deadline) authorization, bytes signature) returns (uint256 amount)',
  'function claimPattern((address wallet, bytes32 patternId, bytes32 nonce, uint256 deadline) authorization, bytes signature) returns (address token, uint256 amount)',
];

export type ClaimStage =
  | 'checking'
  | 'world-id-verification'
  | 'authorization'
  | 'wallet-confirmation'
  | 'transaction-submitted'
  | 'success';

export type ClaimStatus = {
  enrolled: boolean;
  paused: boolean;
  enabled: boolean;
  amount: bigint;
  cap: bigint;
  claimedAmount: bigint;
  inventory: bigint;
  cooldown: bigint;
  lastDirectClaimAt: bigint;
  effectiveTimestamp: bigint;
};

export type ClaimResult = {
  txHash: string;
  userOpHash: string;
  amount: bigint | null;
};

export type ClaimTransactionNotices = {
  enrollment: WorldTransactionNotice;
  directClaim: WorldTransactionNotice;
  patternClaim: WorldTransactionNotice;
};

export type ClaimErrorCode =
  | 'authorization_conflict'
  | 'authorization_expired'
  | 'authorization_wallet_mismatch'
  | 'backend_unavailable'
  | 'claim_cooldown'
  | 'cap_exhausted'
  | 'claim_status_unavailable'
  | 'inventory_insufficient'
  | 'invalid_authorization'
  | 'pattern_not_allowed'
  | 'session_continuity_conflict'
  | 'transaction_cancelled'
  | 'transaction_failed'
  | 'world_verification_failed';

const CLAIM_ERROR_MESSAGES: Record<ClaimErrorCode, string> = {
  authorization_conflict: 'authorization conflict：這張驗證授權與目前請求不一致，請重新驗證。',
  authorization_expired: 'authorization expired：授權已過期，請重新進行 World ID 驗證。',
  authorization_wallet_mismatch: 'authorization wallet 與目前 World App wallet 不一致，已清除暫存授權。',
  backend_unavailable: '無法取得 Claim authorization，請稍後再試。',
  claim_cooldown: 'claim cooldown：目前仍在領取冷卻時間內，請稍後再試。',
  cap_exhausted: 'cap exhausted：BBG 領取額度已達到合約 cap。',
  claim_status_unavailable: '無法讀取鏈上領取狀態，請稍後再試。',
  inventory_insufficient: 'inventory insufficient：BBG 領取庫存暫時不足，請稍後再試。',
  invalid_authorization: 'Claim authorization 回應無效，請重新進行 World ID 驗證。',
  pattern_not_allowed: '此 pattern 目前未被 backend 允許。',
  session_continuity_conflict: 'session continuity conflict：目前 wallet 已有另一個 canonical World ID session，請重新登入後再試。',
  transaction_cancelled: 'transaction cancelled：你已取消 World App 交易。授權仍可在期限內重試。',
  transaction_failed: 'transaction failed：World App 交易未完成。授權仍可在期限內重試。',
  world_verification_failed: 'World verification failed：World ID proof 未能通過官方驗證。',
};

export class WorldIdClaimError extends Error {
  readonly code: ClaimErrorCode;

  constructor(code: ClaimErrorCode, message = CLAIM_ERROR_MESSAGES[code]) {
    super(message);
    this.name = 'WorldIdClaimError';
    this.code = code;
  }
}

export type WorldIdDebugSummary = {
  packageVersion: string | null;
  transport: string | null;
  generatedAt: string | null;
  requestId: string | null;
  miniApp: {
    verifyVersion: string | null;
    platform: string | null;
    sendChannel: string | null;
    minikitSubscribed: boolean | null;
    responseChannel: string | null;
  } | null;
};

export type WorldIdDiagnostic = {
  diagnosticCode: string;
  errorCode: string;
  debugSummary: WorldIdDebugSummary | null;
  appId: string;
  rpId: string;
  action: string | null;
  environment: typeof WORLD_ID_ENVIRONMENT;
  firstVerification: boolean;
  proofReturned: boolean;
  backendVerifyAttempted: boolean;
};

export class WorldIdDiagnosticError extends Error {
  readonly diagnosticCode: string;
  readonly diagnostic: WorldIdDiagnostic;

  constructor(diagnostic: WorldIdDiagnostic) {
    const rejected = new Set(['user_rejected', 'verification_rejected', 'cancelled']);
    const label = rejected.has(diagnostic.errorCode) ? 'World proof rejected' : 'World verification failed';
    super(`${label}：${diagnostic.errorCode}。`);
    this.name = 'WorldIdDiagnosticError';
    this.diagnosticCode = diagnostic.diagnosticCode;
    this.diagnostic = diagnostic;
  }
}

export function getWorldIdDiagnosticCode(error: unknown): string | null {
  return error instanceof WorldIdDiagnosticError ? error.diagnosticCode : null;
}

export function getWorldIdDiagnostic(error: unknown): WorldIdDiagnostic | null {
  return error instanceof WorldIdDiagnosticError ? error.diagnostic : null;
}

function createDiagnosticCode(): string {
  const suffix = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 8).toUpperCase()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`.toUpperCase();
  return `${WORLD_ID_DIAGNOSTIC_PREFIX}-${suffix}`;
}

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as UnknownRecord
    : null;
}

function readOptionalString(record: UnknownRecord, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function readOptionalBoolean(record: UnknownRecord, key: string): boolean | null {
  const value = record[key];
  return typeof value === 'boolean' ? value : null;
}

function toSafeDebugSummary(report: IDKitDebugReport | null, fallbackRequestId: string | null): WorldIdDebugSummary | null {
  if (!report && !fallbackRequestId) return null;
  const root = report as unknown as UnknownRecord | null;
  const miniApp = root ? asRecord(root.mini_app) : null;
  return {
    packageVersion: root ? readOptionalString(root, 'package_version') : null,
    transport: root ? readOptionalString(root, 'transport') : null,
    generatedAt: root ? readOptionalString(root, 'generated_at') : null,
    requestId: (root ? readOptionalString(root, 'request_id') : null) ?? fallbackRequestId,
    miniApp: miniApp
      ? {
          verifyVersion: readOptionalString(miniApp, 'verify_version'),
          platform: readOptionalString(miniApp, 'platform'),
          sendChannel: readOptionalString(miniApp, 'send_channel'),
          minikitSubscribed: readOptionalBoolean(miniApp, 'minikit_subscribed'),
          responseChannel: readOptionalString(miniApp, 'response_channel'),
        }
      : null,
  };
}

function getSafeDebugSummary(request: IDKitRequest): WorldIdDebugSummary | null {
  try {
    return toSafeDebugSummary(request.getDebugReport(), request.requestId ?? null);
  } catch {
    return request.requestId ? toSafeDebugSummary(null, request.requestId) : null;
  }
}

function maskIdentifier(value: string): string {
  const normalized = value.trim();
  if (!normalized) return '未設定';
  const separatorIndex = normalized.indexOf('_');
  const prefix = separatorIndex >= 0 ? normalized.slice(0, separatorIndex + 1) : normalized.slice(0, 2);
  return `${prefix}…${normalized.slice(-4)}`;
}

function buildWorldIdError(
  flow: 'enrollment' | 'session',
  errorCode: string,
  debugSummary: WorldIdDebugSummary | null,
): WorldIdDiagnosticError {
  const diagnostic: WorldIdDiagnostic = {
    diagnosticCode: createDiagnosticCode(),
    errorCode,
    debugSummary,
    appId: maskIdentifier(walletAppConfig.appId),
    rpId: maskIdentifier(walletAppConfig.worldIdRpId),
    action: flow === 'enrollment' ? walletAppConfig.worldIdAction : null,
    environment: WORLD_ID_ENVIRONMENT,
    firstVerification: flow === 'enrollment',
    proofReturned: false,
    backendVerifyAttempted: false,
  };
  console.error(diagnostic);
  return new WorldIdDiagnosticError(diagnostic);
}

function getContract(): ethers.Contract {
  if (!ethers.isAddress(walletAppConfig.bubbleClaimContractAddress)) {
    throw new WorldIdClaimError('claim_status_unavailable', '尚未設定有效的 BubbleClaim 合約地址。');
  }
  return new ethers.Contract(walletAppConfig.bubbleClaimContractAddress, BUBBLE_CLAIM_ABI, worldChainProvider);
}

function getBrowserStorage(): StorageLike | null {
  return typeof window === 'undefined' ? null : window.localStorage;
}

function canonicalWallet(walletAddress: string): string {
  if (!ethers.isAddress(walletAddress)) {
    throw new WorldIdClaimError('claim_status_unavailable', '目前沒有有效的 World App 錢包地址。');
  }
  return ethers.getAddress(walletAddress);
}

function getSignal(flow: 'enrollment' | 'session', walletAddress: string): string {
  const proxy = ethers.getAddress(walletAppConfig.bubbleClaimContractAddress).toLowerCase();
  const wallet = canonicalWallet(walletAddress).toLowerCase();
  const prefix = flow === 'enrollment' ? ENROLLMENT_SIGNAL_PREFIX : SESSION_SIGNAL_PREFIX;
  return `${prefix}${WORLD_CHAIN_ID}:${proxy}:${wallet}`;
}

type RpContextResponse = RpContext & {
  session_id?: string | null;
};

function validateRpContext(context: Partial<RpContextResponse>): asserts context is RpContextResponse {
  if (context.rp_id !== walletAppConfig.worldIdRpId) {
    throw new WorldIdClaimError('world_verification_failed', 'World ID RP context 與目前設定不一致。');
  }
  if (typeof context.signature !== 'string' || context.signature.trim().length === 0) {
    throw new WorldIdClaimError('world_verification_failed', 'World ID RP context 沒有有效簽名。');
  }
  if (
    typeof context.nonce !== 'string'
    || context.nonce.trim().length === 0
    || typeof context.created_at !== 'number'
    || !Number.isFinite(context.created_at)
    || typeof context.expires_at !== 'number'
    || !Number.isFinite(context.expires_at)
    || context.expires_at <= context.created_at
  ) {
    throw new WorldIdClaimError('world_verification_failed', 'World ID RP context 格式不正確。');
  }
}

async function getRpContext(
  flow: 'enrollment' | 'session',
  walletAddress?: string,
): Promise<{ context: RpContext; canonicalSessionId: WorldIdSessionId | null }> {
  if (!supabase) throw new WorldIdClaimError('backend_unavailable', '尚未設定 Supabase，無法取得 World ID RP context。');
  if (!/^rp_[a-zA-Z0-9]+$/.test(walletAppConfig.worldIdRpId)) {
    throw new WorldIdClaimError('world_verification_failed', '尚未設定有效的 World ID RP ID。');
  }

  const body = {
    flow,
    ...(flow === 'session' && walletAddress ? { wallet_address: canonicalWallet(walletAddress) } : {}),
  };
  const { data, error } = await supabase.functions.invoke('world-id-rp-context', { body });
  if (error || !data || typeof data !== 'object') {
    throw new WorldIdClaimError('backend_unavailable', '無法取得 World ID RP context，請稍後再試。');
  }
  const context = data as Partial<RpContextResponse>;
  validateRpContext(context);
  const canonicalSessionId = context.session_id && /^session_[0-9a-f]{128}$/i.test(context.session_id)
    ? context.session_id as WorldIdSessionId
    : null;
  if (context.session_id !== undefined && context.session_id !== null && !canonicalSessionId) {
    throw new WorldIdClaimError('world_verification_failed', 'World ID canonical session_id 格式不正確。');
  }
  const rpContext = { ...context } as RpContextResponse;
  delete rpContext.session_id;
  return { context: rpContext, canonicalSessionId };
}

function getConfiguredAppId(): `app_${string}` {
  if (!/^app_[a-zA-Z0-9]+$/.test(walletAppConfig.appId)) {
    throw new WorldIdClaimError('world_verification_failed', '尚未設定有效的 World App App ID。');
  }
  return walletAppConfig.appId as `app_${string}`;
}

async function requestEnrollment(walletAddress: string): Promise<IDKitResult> {
  const appId = getConfiguredAppId();
  const { context: rpContext } = await getRpContext('enrollment');
  const builder = IDKit.request({
    app_id: appId,
    action: walletAppConfig.worldIdAction,
    action_description: 'Enroll to claim Bubble rewards',
    rp_context: rpContext,
    allow_legacy_proofs: false,
    environment: WORLD_ID_ENVIRONMENT,
  });
  const request = await builder.constraints(any(CredentialRequest(PROOF_OF_HUMAN, {
    signal: getSignal('enrollment', walletAddress),
  })));
  const completion = await request.pollUntilCompletion({ timeout: 120_000 });
  if (!completion.success) {
    throw buildWorldIdError('enrollment', completion.error, getSafeDebugSummary(request));
  }
  return completion.result;
}

const activeSessionIds = new Map<string, `session_${string}`>();

async function requestSession(walletAddress: string): Promise<IDKitResult> {
  const appId = getConfiguredAppId();
  const wallet = canonicalWallet(walletAddress);
  const { context: rpContext, canonicalSessionId } = await getRpContext('session', wallet);
  const key = wallet.toLowerCase();
  const storage = getBrowserStorage();
  const cachedSessionId = storage && ethers.isAddress(walletAppConfig.bubbleClaimContractAddress)
    ? readWorldIdSessionId(storage, walletAppConfig.bubbleClaimContractAddress, wallet, walletAppConfig.worldIdRpId)
    : null;
  // The backend canonical binding wins. A local value is only a cache/fallback
  // for the first binding attempt; the issuer/database remains authoritative.
  const sessionId = selectWorldIdSessionId(canonicalSessionId, cachedSessionId, activeSessionIds.get(key) ?? null);
  const config = {
    app_id: appId,
    action_description: 'Verify to claim your Bubble reward',
    rp_context: rpContext,
    environment: WORLD_ID_ENVIRONMENT,
  } as const;
  const builder = sessionId ? IDKit.proveSession(sessionId, config) : IDKit.createSession(config);
  const request = await builder.constraints(any(CredentialRequest(PROOF_OF_HUMAN, {
    signal: getSignal('session', walletAddress),
  })));
  const completion = await request.pollUntilCompletion({ timeout: 120_000 });
  if (!completion.success) {
    throw buildWorldIdError('session', completion.error, getSafeDebugSummary(request));
  }
  if ('session_id' in completion.result && typeof completion.result.session_id === 'string') {
    if (!/^session_[0-9a-f]{128}$/i.test(completion.result.session_id)) {
      throw new WorldIdClaimError('world_verification_failed', 'World ID session_id 格式不正確。');
    }
    const completedSessionId = completion.result.session_id as WorldIdSessionId;
    activeSessionIds.set(key, completedSessionId);
    if (storage && ethers.isAddress(walletAppConfig.bubbleClaimContractAddress)) {
      writeWorldIdSessionId(storage, walletAppConfig.bubbleClaimContractAddress, wallet, completedSessionId, walletAppConfig.worldIdRpId);
    }
  }
  return completion.result;
}

export async function getBbgClaimStatus(walletAddress: string): Promise<ClaimStatus> {
  const wallet = canonicalWallet(walletAddress);
  const storage = getBrowserStorage();
  if (storage && ethers.isAddress(walletAppConfig.bubbleClaimContractAddress)) {
    // Reading with a new wallet removes any cached authorization belonging to
    // the previous wallet before another claim flow can start.
    readClaimRecovery(storage, walletAppConfig.bubbleClaimContractAddress, wallet);
  }
  const contract = getContract();
  try {
    const [enrolled, paused, config, inventory, lastDirectClaimAt, effectiveTimestamp] = await Promise.all([
      contract.enrolled(wallet) as Promise<boolean>,
      contract.paused() as Promise<boolean>,
      contract.tokenConfigs(ON_CHAIN_TOKEN_ADDRESSES.BBG) as Promise<{
        enabled: boolean;
        claimAmount: bigint;
        cap: bigint;
        claimedAmount: bigint;
        cooldown: bigint;
      }>,
      new ethers.Contract(
        ON_CHAIN_TOKEN_ADDRESSES.BBG,
        ['function balanceOf(address) view returns (uint256)'],
        worldChainProvider,
      ).balanceOf(walletAppConfig.bubbleClaimContractAddress) as Promise<bigint>,
      contract.lastDirectClaimAt(ON_CHAIN_TOKEN_ADDRESSES.BBG, wallet) as Promise<bigint>,
      contract.effectiveTimestamp() as Promise<bigint>,
    ]);

    return {
      enrolled,
      paused,
      enabled: config.enabled,
      amount: config.claimAmount,
      cap: config.cap,
      claimedAmount: config.claimedAmount,
      inventory,
      cooldown: config.cooldown,
      lastDirectClaimAt,
      effectiveTimestamp,
    };
  } catch (error) {
    if (error instanceof WorldIdClaimError) throw error;
    throw new WorldIdClaimError('claim_status_unavailable');
  }
}

function getRecoveryRecord(walletAddress: string): ClaimRecoveryRecord | null {
  const storage = getBrowserStorage();
  if (!storage || !ethers.isAddress(walletAppConfig.bubbleClaimContractAddress)) return null;
  return readClaimRecovery(storage, walletAppConfig.bubbleClaimContractAddress, walletAddress);
}

function saveRecoveryRecord(record: ClaimRecoveryRecord): void {
  const storage = getBrowserStorage();
  if (!storage) return;
  writeClaimRecovery(storage, walletAppConfig.bubbleClaimContractAddress, record);
}

function clearRecoveryRecord(): void {
  const storage = getBrowserStorage();
  if (storage && ethers.isAddress(walletAppConfig.bubbleClaimContractAddress)) {
    clearClaimRecovery(storage, walletAppConfig.bubbleClaimContractAddress);
  }
}

export function clearWorldIdClaimRecovery(): void {
  const storage = getBrowserStorage();
  if (storage && ethers.isAddress(walletAppConfig.bubbleClaimContractAddress)) {
    clearWorldIdSessionId(storage, walletAppConfig.bubbleClaimContractAddress);
  }
  clearRecoveryRecord();
  activeSessionIds.clear();
}

function isMatchingRecovery(
  record: ClaimRecoveryRecord,
  flow: ClaimFlow,
  walletAddress: string,
  patternId?: string,
): boolean {
  if (record.flow !== flow || record.wallet.toLowerCase() !== walletAddress.toLowerCase()) return false;
  if (flow === 'pattern_claim') {
    return record.patternId?.toLowerCase() === patternId?.toLowerCase();
  }
  return true;
}

async function findUsableRecovery(
  flow: ClaimFlow,
  walletAddress: string,
  patternId?: string,
): Promise<ClaimRecoveryRecord | null> {
  const record = getRecoveryRecord(walletAddress);
  if (!record || !isMatchingRecovery(record, flow, walletAddress, patternId)) return null;
  if (isAuthorizationExpired(record.deadline)) {
    clearRecoveryRecord();
    return null;
  }

  try {
    const consumed = await getContract().isAuthorizationConsumed(record.authorizationDigest) as boolean;
    if (consumed) {
      clearRecoveryRecord();
      return null;
    }
  } catch {
    throw new WorldIdClaimError('claim_status_unavailable');
  }
  return record;
}

function readFunctionErrorCode(error: unknown): Promise<string | null> {
  const context = asRecord(error)?.context;
  if (context instanceof Response) {
    return context.clone().json()
      .then((payload: unknown) => {
        const record = asRecord(payload);
        return typeof record?.error === 'string' ? record.error : null;
      })
      .catch(() => null);
  }
  return Promise.resolve(null);
}

function mapBackendError(code: string | null): WorldIdClaimError {
  switch (code) {
    case 'authorization_conflict':
    case 'replay_conflict':
    case 'authorization_replay_conflict':
      return new WorldIdClaimError('authorization_conflict');
    case 'SESSION_BINDING_CONFLICT':
    case 'session_binding_conflict':
    case 'session_continuity_conflict':
      return new WorldIdClaimError('session_continuity_conflict');
    case 'authorization_expired':
    case 'replay_reservation_expired':
      return new WorldIdClaimError('authorization_expired');
    case 'pattern_not_allowed':
      return new WorldIdClaimError('pattern_not_allowed');
    case 'world_id_verification_unavailable':
    case 'replay_storage_unavailable':
    case 'replay_storage_invalid':
    case 'internal_error':
      return new WorldIdClaimError('backend_unavailable');
    case 'world_id_verification_failed':
    case 'world_id_signal_mismatch':
    case 'world_id_action_mismatch':
    case 'world_id_environment_mismatch':
    case 'unsupported_world_id_protocol':
    case 'invalid_idkit_response':
      return new WorldIdClaimError('world_verification_failed');
    default:
      return new WorldIdClaimError('invalid_authorization');
  }
}

async function invokeAuthorization(
  flow: ClaimFlow,
  walletAddress: string,
  idempotencyKey: string,
  idkitResponse: IDKitResult,
  patternId?: string,
): Promise<ReturnType<typeof parseClaimAuthorizationResponse>> {
  if (!supabase) throw new WorldIdClaimError('backend_unavailable');
  const { data, error } = await supabase.functions.invoke('world-id-verify-authorization', {
    body: {
      flow,
      wallet_address: canonicalWallet(walletAddress),
      idempotency_key: idempotencyKey,
      ...(flow === 'pattern_claim' ? { pattern_id: patternId } : {}),
      // This is the exact IDKit completion result. No proof field is reconstructed in the browser.
      idkit_response: idkitResponse,
    },
  });
  if (error || !data || typeof data !== 'object') {
    throw mapBackendError(await readFunctionErrorCode(error));
  }
  const result = asRecord(data);
  if (result?.status === 'conflict') throw new WorldIdClaimError('authorization_conflict');
  try {
    const parsed = parseClaimAuthorizationResponse(data, flow, walletAddress, patternId);
    const expectedDigest = computeClaimAuthorizationDigest(
      parsed.flow,
      parsed.authorization,
      WORLD_CHAIN_ID,
      walletAppConfig.bubbleClaimContractAddress,
    );
    if (expectedDigest.toLowerCase() !== parsed.authorizationDigest.toLowerCase()) {
      throw new WorldIdClaimError('invalid_authorization', 'Claim authorization digest 與 signed authorization 不一致。');
    }
    return parsed;
  } catch (parseError) {
    if (parseError instanceof Error && parseError.message.includes('wallet')) {
      throw new WorldIdClaimError('authorization_wallet_mismatch');
    }
    throw new WorldIdClaimError('invalid_authorization');
  }
}

function isRetryableBackendError(error: unknown): boolean {
  return error instanceof WorldIdClaimError && error.code === 'backend_unavailable';
}

async function getOrIssueAuthorization(
  flow: ClaimFlow,
  walletAddress: string,
  onStage: ((stage: ClaimStage) => void) | undefined,
  patternId?: string,
): Promise<ClaimRecoveryRecord> {
  const cached = await findUsableRecovery(flow, walletAddress, patternId);
  if (cached) return cached;

  const idempotencyKey = createIdempotencyKey();
  onStage?.('world-id-verification');
  const idkitResponse = flow === 'enrollment'
    ? await requestEnrollment(walletAddress)
    : await requestSession(walletAddress);

  onStage?.('authorization');
  let parsed;
  try {
    parsed = await invokeAuthorization(flow, walletAddress, idempotencyKey, idkitResponse, patternId);
  } catch (error) {
    if (!isRetryableBackendError(error)) throw error;
    // Retry the same proof lifecycle with the same idempotency key.
    parsed = await invokeAuthorization(flow, walletAddress, idempotencyKey, idkitResponse, patternId);
  }
  const record = createClaimRecoveryRecord(parsed, idempotencyKey);
  if (isAuthorizationExpired(record.deadline)) {
    throw new WorldIdClaimError('authorization_expired');
  }
  saveRecoveryRecord(record);
  return record;
}

function encodeAuthorization(
  contract: ethers.Contract,
  flow: ClaimFlow,
  authorization: ClaimAuthorization,
  signature: string,
): string {
  if (flow === 'enrollment') return contract.interface.encodeFunctionData('enroll', [authorization, signature]);
  if (flow === 'direct_claim') return contract.interface.encodeFunctionData('claim', [authorization, signature]);
  return contract.interface.encodeFunctionData('claimPattern', [authorization, signature]);
}

async function completeEnrollment(
  walletAddress: string,
  onStage?: (stage: ClaimStage) => void,
  notice?: WorldTransactionNotice,
): Promise<void> {
  const authorization = await getOrIssueAuthorization('enrollment', walletAddress, onStage);
  onStage?.('wallet-confirmation');
  try {
    await sendWorldTransaction(
      [{
        to: walletAppConfig.bubbleClaimContractAddress,
        data: encodeAuthorization(getContract(), 'enrollment', authorization.authorization, authorization.signature),
      }],
      () => onStage?.('transaction-submitted'),
      notice,
    );
  } catch (error) {
    throw normalizeTransactionError(error);
  }

  const status = await getBbgClaimStatus(walletAddress);
  if (!status.enrolled) {
    throw new WorldIdClaimError('transaction_failed', 'enrollment 交易已確認，但鏈上尚未顯示 enrolled(wallet)。');
  }
  clearRecoveryRecord();
}

function normalizeTransactionError(error: unknown): WorldIdClaimError {
  if (error instanceof WorldTransactionError) {
    return new WorldIdClaimError(error.code, error.message);
  }
  if (error instanceof WorldIdClaimError) return error;
  return new WorldIdClaimError('transaction_failed', error instanceof Error ? error.message : undefined);
}

function throwIfDirectPreflightFails(status: ClaimStatus): void {
  if (!status.enabled) throw new WorldIdClaimError('claim_status_unavailable', 'BBG 每日領取目前未開放。');
  if (status.amount === 0n) throw new WorldIdClaimError('claim_status_unavailable', 'BBG 領取數量尚未設定。');
  if (status.inventory < status.amount) throw new WorldIdClaimError('inventory_insufficient');
  if (status.claimedAmount > status.cap || status.amount > status.cap - status.claimedAmount) {
    throw new WorldIdClaimError('cap_exhausted');
  }
  if (
    status.lastDirectClaimAt > 0n
    && status.effectiveTimestamp < status.lastDirectClaimAt + status.cooldown
  ) {
    throw new WorldIdClaimError('claim_cooldown');
  }
}

export async function claimBbg(
  walletAddress: string,
  onStage?: (stage: ClaimStage) => void,
  notices?: ClaimTransactionNotices,
): Promise<ClaimResult> {
  const wallet = canonicalWallet(walletAddress);
  getConfiguredAppId();
  let status = await getBbgClaimStatus(wallet);
  throwIfDirectPreflightFails(status);

  if (!status.enrolled) {
    await completeEnrollment(wallet, onStage, notices?.enrollment);
    status = await getBbgClaimStatus(wallet);
  }

  const authorization = await getOrIssueAuthorization('direct_claim', wallet, onStage);
  onStage?.('wallet-confirmation');
  let txHash: string;
  let userOpHash: string;
  try {
    ({ txHash, userOpHash } = await sendWorldTransaction(
      [{
        to: walletAppConfig.bubbleClaimContractAddress,
        data: encodeAuthorization(getContract(), 'direct_claim', authorization.authorization, authorization.signature),
      }],
      () => onStage?.('transaction-submitted'),
      notices?.directClaim,
    ));
  } catch (error) {
    throw normalizeTransactionError(error);
  }
  clearRecoveryRecord();
  onStage?.('success');
  return { txHash, userOpHash, amount: status.amount };
}

export async function claimPattern(
  walletAddress: string,
  patternId: string,
  onStage?: (stage: ClaimStage) => void,
  notices?: ClaimTransactionNotices,
): Promise<ClaimResult> {
  const wallet = canonicalWallet(walletAddress);
  getConfiguredAppId();
  if (!ethers.isHexString(patternId, 32) || BigInt(patternId) === 0n) {
    throw new WorldIdClaimError('pattern_not_allowed', 'patternId 格式不正確。');
  }
  const status = await getBbgClaimStatus(wallet);
  if (!status.enrolled) await completeEnrollment(wallet, onStage, notices?.enrollment);

  const authorization = await getOrIssueAuthorization('pattern_claim', wallet, onStage, patternId);
  onStage?.('wallet-confirmation');
  let txHash: string;
  let userOpHash: string;
  try {
    ({ txHash, userOpHash } = await sendWorldTransaction(
      [{
        to: walletAppConfig.bubbleClaimContractAddress,
        data: encodeAuthorization(getContract(), 'pattern_claim', authorization.authorization, authorization.signature),
      }],
      () => onStage?.('transaction-submitted'),
      notices?.patternClaim,
    ));
  } catch (error) {
    throw normalizeTransactionError(error);
  }
  clearRecoveryRecord();
  onStage?.('success');
  return { txHash, userOpHash, amount: null };
}
